<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    :id="id"
    class="modal"
    :class="className">
    <div
      class="modal-dialog modal-full-width"
      role="document">
      <div
        class="modal-content"
        style="min-height: 53rem">
        <div class="modal-header">
          <h5 class="modal-title">
            <template v-if="isMobile">
              {{ capitalize($t('actions.linkToMobileCreative')) }}
            </template>
            <template v-else>
              {{ capitalize($t('actions.linkToCreative')) }}
            </template>
          </h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
            @click="cancel">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ $t('messages.selectOneOrMoreCreatives') }}</p>
          <div id="link_creatives_list_table_container">
            <b-table
              id="link_creatives_to_line_item_creatives_list_table"
              ref="table"
              category="LineItems > Link Creatives"
              :items="creatives"
              :columns="columns"
              :show-filters="true"
              :fixed-filters="fixedFilters"
              :total-count="totalCount"
              :text-search-properties="textSearchProperties"
              :is-loading="isLoading"
              data-row-height="11rem"
              pagination
              allow-selection
              @filters-changed="onTableFiltersChanged"
              @selection-changed="selectedCreatives = $event">
              <template #preview="item">
                <creative-preview
                  :creative-file="item.item.creativeFileForPreview"
                  size="lg"
                  @preview-clicked="onCreativePreviewClicked"
                  @thumbnail-clicked="onCreativeThumbnailClicked" />
              </template>
              <template #link="item">
                <missing-creative-size-alert-icon
                  v-if="item.item.isMissingCreativeSizes"
                  :missing-creative-sizes="item.item.missingCreativeSizes" />
                {{ item.item.name }}
              </template>
              <template #status="item">
                <creative-status-icon :status="item.item.status" />
                {{ capitalize($t(`states.${item.item.status}`)) }}
              </template>
              <template #no-records>
                <no-records-table
                  :icon="markRaw(IconComplexCreative)"
                  :header-text="$t('messages.tablePlaceholderNoCreativesTitle')"
                  :details-text="$t('messages.tablePlaceholderNoCreativesDescription')" />
              </template>
              <template #no-match>
                <no-records-table
                  :icon="markRaw(IconComplexCreative)"
                  :header-text="$t('messages.tablePlaceholderNoMatchingCreativesTitle')"
                  :details-text="$t('messages.tablePlaceholderNoMatchingItemsDescription')" />
              </template>
            </b-table>
          </div>
        </div>
        <div class="modal-footer">
          <button
            id="save_creatives_link_btn"
            type="button"
            class="btn btn-primary"
            @click="confirm">
            {{ $t('actions.saveChanges') }}
          </button>
          <button
            type="button"
            class="btn btn-default"
            data-dismiss="modal"
            @click="cancel">
            {{ $t('actions.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <full-screen-image
      :is-visible="isCreativeThumbnailModalVisible"
      :image-url="activeCreativeThumbnailUrl"
      :name="activeCreativeFileName"
      @close="isCreativeThumbnailModalVisible = false" />
    <full-screen-video
      :is-visible="isCreativePreviewModalVisible"
      :video-url="activeCreativePreviewUrl"
      :name="activeCreativeFileName"
      @close="isCreativePreviewModalVisible = false" />
  </div>
</template>

<script type="text/babel">
  import { CanceledError } from 'axios';
  import { capitalize, defineAsyncComponent, markRaw } from 'vue';
  import { mapActions } from 'vuex';

  import { Table } from '@/common/components/controls';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import CreativePreview from '@/common/components/creatives/CreativePreview';
  import CreativeType from '@/common/enums/CreativeType.enum';
  import { IconComplexCreative } from '@/common/icons/complex';
  import { Filter } from '@/common/models';
  import CreativeOptions from '@/common/models/creative/CreativeOptions';
  import CreativeStatus from '@/common/models/creative/CreativeStatus.enum';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import ErrorHandler from '@/ErrorHandler';
  import CreativeStatusIcon from '@/org-app/components/creatives/CreativeStatusIcon';
  import i18n from '@/vueI18n';

  export default {
    components: {
      CreativePreview,
      CreativeStatusIcon,
      FullScreenImage: defineAsyncComponent(() => import('@/org-app/components/controls/FullScreenImage')),
      FullScreenVideo: defineAsyncComponent(() => import('@/org-app/components/controls/FullScreenVideo')),
      'b-table': Table,
      NoRecordsTable,
    },
    props: {
      id: {
        type: String,
        default: null,
      },
      visible: {
        type: Boolean,
        default: false,
      },
      advertiserID: {
        type: Number,
        default: null,
      },
      lineItemID: {
        type: Number,
        default: null,
      },
      mobileLineItemID: {
        type: Number,
        default: null,
      },
      isMobile: {
        type: Boolean,
        default: false,
      },
      isDisplayVideo: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['confirmed', 'cancelled'],
    data() {
      let fixedFilters = [
        new Filter('is_fallback', [false], FilterComparisonOperator.EQUAL),
        new Filter('state', [CreativeStatus.RETIRED], FilterComparisonOperator.NOT_EQUAL),
        new Filter('advertiser_id', [this.advertiserID], FilterComparisonOperator.EQUAL),
      ];
      if (this.lineItemID) {
        fixedFilters = [
          ...fixedFilters,
          new Filter('not lineitem_creatives.lineitem_id', [this.lineItemID], FilterComparisonOperator.IN),
          new Filter('is_mobile', [false], FilterComparisonOperator.EQUAL),
        ];
      } else if (this.mobileLineItemID) {
        fixedFilters = [
          ...fixedFilters,
          new Filter(
            'not mobilelineitem_creatives.mobilelineitem_id',
            [this.mobileLineItemID],
            FilterComparisonOperator.IN,
          ),
          new Filter('is_mobile', [true], FilterComparisonOperator.EQUAL),
        ];
      }

      if (this.isDisplayVideo) {
        fixedFilters = [
          ...fixedFilters,
          new Filter('creative_type', [CreativeType.Banner, CreativeType.Video], FilterComparisonOperator.IN),
        ];
      } else {
        fixedFilters = [...fixedFilters, new Filter('creative_type', [null], FilterComparisonOperator.EQUAL)];
      }
      return {
        fixedFilters,
        selectedCreatives: [],
        creatives: [],
        totalCount: 0,
        textSearchProperties: ['name', 'advertiser.name'],
        isLoading: true,
        activeCreativeThumbnailUrl: null,
        activeCreativeFileName: null,
        isCreativeThumbnailModalVisible: false,
        activeCreativePreviewUrl: null,
        isCreativePreviewModalVisible: false,
        IconComplexCreative,
      };
    },
    computed: {
      columns() {
        return {
          preview: TableColumn.fromObject({ label: capitalize(i18n.global.t('names.thumbnail', { n: 1 })) }),
          link: TableColumn.fromObject({
            label: capitalize(i18n.global.t('names.name', { n: 1 })),
            property: 'name',
            type: TableColumnTypes.string,
          }),
          advertiserDescription: TableColumn.fromObject({
            label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
            property: 'advertiser.name',
            type: TableColumnTypes.string,
          }),
          status: TableColumn.fromObject({
            label: capitalize(i18n.global.t('names.status', { n: 1 })),
            property: 'state',
            source: CreativeOptions.options().map((x) => ({ id: x.value, name: x.name, filters: x.filters })),
          }),
        };
      },
      className() {
        if (this.visible) {
          return 'visible';
        }
        return 'fade';
      },
    },
    mounted() {
      this.fetchCreatives(this.$refs.table?.queryParams);
    },
    methods: {
      markRaw,
      ...mapActions('orgApp', ['getCreatives']),
      capitalize(x) {
        return capitalize(x);
      },
      confirm() {
        this.$emit('confirmed', this.selectedCreatives);
      },
      cancel() {
        this.$emit('cancelled');
      },
      async fetchCreatives(queryParams) {
        try {
          this.isLoading = true;
          const result = await queryParams.run(this.getCreatives);
          this.totalCount = result.totalCount;
          this.creatives = result.items;
          this.isLoading = false;
        } catch (e) {
          if (!(e instanceof CanceledError)) this.isLoading = false;
          ErrorHandler.handleExceptionInUIComponent(e);
        }
      },
      onTableFiltersChanged(queryParams) {
        this.fetchCreatives(queryParams);
      },
      onCreativeThumbnailClicked(e) {
        this.activeCreativeThumbnailUrl = e.target.src;
        this.isCreativeThumbnailModalVisible = true;
        this.creatives.forEach((creative) => {
          const cf = creative.creativeFiles.find((x) => x.imageThumbnailUrl === this.activeCreativeThumbnailUrl);
          if (cf !== undefined) {
            this.activeCreativeFileName = cf.name;
          }
        });
      },
      onCreativePreviewClicked(e) {
        this.activeCreativePreviewUrl = e.target.src;
        this.isCreativePreviewModalVisible = true;
        this.creatives.forEach((creative) => {
          const cf = creative.creativeFiles.find((x) => x.videoPreviewUrl === this.activeCreativePreviewUrl);
          if (cf !== undefined) {
            this.activeCreativeFileName = cf.name;
          }
        });
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped></style>
